import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReportLayout from '@/pages/reports/components/ReportLayout';
import ReportTable from '@/pages/reports/components/ReportTable';
import SummaryCards from '@/pages/reports/components/SummaryCards';
import { useStockSummary } from '@/hooks/useReports';
import { useWarehouseStore } from '@/stores/warehouseStore';

import axios from 'axios';

export const getStockSummaryApi = async (
  warehouseId?: string,
  categoryId?: string,
) => {
  try {
    const token = localStorage.getItem('token');

    const res = await axios.get(
      `${import.meta.env.VITE_API_URL}/api/v1/reports/stock-summary`,
      {
        params: { warehouseId, categoryId },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return res?.data?.data ?? []; // ✅ ALWAYS array
  } catch (err) {
    console.error('Stock Summary API Error:', err);
    return []; // ✅ NEVER undefined
  }
};

export const getItemBatchesApi = async (itemId: string, warehouseId?: string) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.get(
      `${import.meta.env.VITE_API_URL}/api/v1/reports/stock-summary/${itemId}/batches`,
      {
        params: { warehouseId },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data?.data || [];
  } catch (err) {
    console.error('getItemBatchesApi error:', err);
    return [];
  }
};

export const getItemSerialsApi = async (itemId: string, warehouseId?: string) => {
  try {
    const token = localStorage.getItem('token');
    const validWh = warehouseId && warehouseId !== 'ALL' ? warehouseId : undefined;
    const res = await axios.get(
      `${import.meta.env.VITE_API_URL}/api/v1/reports/stock-summary/${itemId}/serials`,
      {
        params: { warehouseId: validWh },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
      return res.data.data;
    }
    // If empty and warehouse filter was active, try without warehouse filter
    if (validWh) {
      const resAll = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/reports/stock-summary/${itemId}/serials`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (resAll.data?.success && Array.isArray(resAll.data.data)) {
        return resAll.data.data;
      }
    }
    return res.data?.data || [];
  } catch (err) {
    console.error('getItemSerialsApi error:', err);
    return [];
  }
};

export const getCategories = async () => {
  const token = localStorage.getItem('token');

  const res = await axios.get(
    `${import.meta.env.VITE_API_URL}/api/v1/categories/all`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return res.data.data;
};

type StockStatus = 'NORMAL' | 'LOW' | 'CRITICAL';

function formatINR(n: number) {
  return `₹${n.toLocaleString('en-IN')}`;
}

const STATUS_META: Record<StockStatus, { label: string; cls: string }> = {
  NORMAL: { label: 'Normal', cls: 'bg-green-100 text-green-700' },
  LOW: { label: 'Low Stock', cls: 'bg-amber-100 text-amber-700' },
  CRITICAL: { label: 'Critical', cls: 'bg-red-100 text-red-700' },
};

export default function StockSummaryReport() {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const { selectedWarehouseId } = useWarehouseStore();
  const warehouseId =
    selectedWarehouseId && selectedWarehouseId !== 'ALL'
      ? selectedWarehouseId
      : '';
  const [categoryId, setCategoryId] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [generated, setGenerated] = useState(false);
  const [categories, setCategories] = useState([]);

  // Batch breakdown drawer state
  const [selectedItemForBatches, setSelectedItemForBatches] = useState<any | null>(null);
  const [itemBatches, setItemBatches] = useState<any[]>([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  // Serial breakdown drawer state
  const [selectedItemForSerials, setSelectedItemForSerials] = useState<any | null>(null);
  const [itemSerials, setItemSerials] = useState<any[]>([]);
  const [loadingSerials, setLoadingSerials] = useState(false);

  const handleOpenBatchDrawer = async (item: any) => {
    setSelectedItemForBatches(item);
    setLoadingBatches(true);
    try {
      const batches = await getItemBatchesApi(item.id, warehouseId);
      setItemBatches(batches);
    } catch (err) {
      console.error(err);
      setItemBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  };

  const handleOpenSerialDrawer = async (item: any) => {
    setSelectedItemForSerials(item);
    setLoadingSerials(true);
    try {
      const serials = await getItemSerialsApi(item.id, warehouseId);
      setItemSerials(serials);
    } catch (err) {
      console.error(err);
      setItemSerials([]);
    } finally {
      setLoadingSerials(false);
    }
  };

  const {
    data = [],
    isFetching,
    refetch,
  } = useStockSummary(warehouseId, categoryId, generated);

  useEffect(() => {
    const loadData = async () => {
      try {
        const c = await getCategories();
        setCategories(Array.isArray(c) ? c : []);
      } catch (err) {
        console.error('Failed to load filters', err);
      }
    };

    loadData();
  }, []);

  const handleGenerate = () => {
    setGenerated(true);
    refetch();
  };

  const filtered = data.filter((r) => {
    if (statusFilter === 'low')
      return r.status === 'LOW' || r.status === 'CRITICAL';
    if (statusFilter === 'critical') return r.status === 'CRITICAL';
    if (statusFilter === 'normal') return r.status === 'NORMAL';
    return true;
  });

  const groupedMap = new Map();

  filtered.forEach((row: any) => {
    const key = row.code;

    if (!groupedMap.has(key)) {
      groupedMap.set(key, {
        ...row,
        isBatchTracked: Boolean(row.isBatchTracked || row.is_batch_tracked),
        isSerialTracked: Boolean(row.isSerialTracked || row.is_serial_tracked),
        currentStock: Number(row.currentStock) || 0,
        stockValue: Number(row.stockValue) || 0,
        openingStock: Number(row.openingStock) || 0,
        inQty: Number(row.inQty) || 0,
        outQty: Number(row.outQty) || 0,
      });
    } else {
      const existing = groupedMap.get(key);

      existing.currentStock += Number(row.currentStock) || 0;
      existing.stockValue += Number(row.stockValue) || 0;
      existing.openingStock = (existing.openingStock || 0) + (Number(row.openingStock) || 0);
      existing.inQty += Number(row.inQty) || 0;
      existing.outQty += Number(row.outQty) || 0;
      if (row.isBatchTracked || row.is_batch_tracked) existing.isBatchTracked = true;
      if (row.isSerialTracked || row.is_serial_tracked) existing.isSerialTracked = true;
    }
  });

  const groupedData = Array.from(groupedMap.values());
  const totalValue = groupedData.reduce(
    (sum, item) => sum + item.stockValue,
    0,
  );

  const lowCount = groupedData.filter(
    (r) => r.status === 'LOW' || r.status === 'CRITICAL',
  ).length;

  const exportColumns = [
    { header: 'Code', key: 'code', width: 14 },
    { header: 'Item Name', key: 'name', width: 28 },
    { header: 'Category', key: 'category', width: 20 },
    { header: 'Unit', key: 'unit', width: 10 },
    { header: 'Opening', key: 'openingStock', width: 12 },
    { header: 'In Qty', key: 'inQty', width: 12 },
    { header: 'Out Qty', key: 'outQty', width: 12 },
    { header: 'Current Stock', key: 'currentStock', width: 14 },
    { header: 'Min Level', key: 'minLevel', width: 12 },
    { header: 'Stock Value', key: 'stockValue', width: 16 },
    { header: 'Status', key: 'status', width: 14 },
  ];

  const filterBar = (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label className="block text-xs font-medium text-[#64748b] mb-1">
          Category
        </label>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="h-8 px-2 border border-[#e2e8f0] rounded-lg text-sm bg-white text-[#1e293b] cursor-pointer focus:outline-none focus:border-[#4f46e5]"
        >
          <option value="">All Categories</option>
          {categories.map((c: any) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-[#64748b] mb-1">
          Status
        </label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-8 px-2 border border-[#e2e8f0] rounded-lg text-sm bg-white text-[#1e293b] cursor-pointer focus:outline-none focus:border-[#4f46e5]"
        >
          <option value="all">All Status</option>
          <option value="normal">Normal</option>
          <option value="low">Low Stock</option>
          <option value="critical">Critical</option>
        </select>
      </div>
    </div>
  );

  const getSerialStatusBadge = (status: string) => {
    switch (status) {
      case 'IN_STOCK':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">IN STOCK ✅</span>;
      case 'SOLD':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">SOLD 🔵</span>;
      case 'RETURNED':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">RETURNED 🔄</span>;
      case 'DEFECTIVE':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">DEFECTIVE ⚠️</span>;
      case 'IN_REPAIR':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-50 text-purple-700 border border-purple-200">IN REPAIR 🔧</span>;
      default:
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">{status || 'AVAILABLE'}</span>;
    }
  };

  return (
    <ReportLayout
      title="Stock Summary Report"
      subtitle={`As of ${today} · All movement types included`}
      filterBar={filterBar}
      onGenerate={handleGenerate}
      generating={isFetching}
      generated={generated}
      exportData={filtered as unknown as Record<string, unknown>[]}
      exportColumns={exportColumns}
      exportFilename="stock-summary"
    >
      {generated && (
        <>
          <SummaryCards
            cards={[
              {
                label: 'Total Items',
                value: filtered.length,
                icon: 'ri-box-3-line',
                color: 'bg-indigo-50 text-[#4f46e5]',
              },
              {
                label: 'Total Stock Value',
                value: formatINR(totalValue),
                icon: 'ri-money-rupee-circle-line',
                color: 'bg-green-50 text-green-600',
              },
              {
                label: 'Low Stock Items',
                value: lowCount,
                icon: 'ri-alert-line',
                color:
                  lowCount > 0
                    ? 'bg-red-50 text-red-500'
                    : 'bg-slate-100 text-slate-500',
                sub: lowCount > 0 ? 'Needs reorder' : 'All good',
              },
              {
                label: 'Normal Stock Items',
                value: filtered.length - lowCount,
                icon: 'ri-checkbox-circle-line',
                color: 'bg-emerald-50 text-emerald-600',
              },
            ]}
          />
          <ReportTable
            keyField="id"
            data={groupedData}
            loading={isFetching}
            columns={[
              {
                header: 'Code',
                accessor: 'code',
                className: 'font-mono text-xs text-[#64748b]',
              },
              {
                header: 'Item Name',
                accessor: (r) => {
                  const hasBatch = Boolean(r.isBatchTracked || r.is_batch_tracked);
                  const hasSerial = Boolean(r.isSerialTracked || r.is_serial_tracked);

                  if (hasBatch) {
                    return (
                      <button
                        type="button"
                        onClick={() => handleOpenBatchDrawer(r)}
                        className="font-medium text-left text-indigo-600 hover:text-indigo-800 hover:underline max-w-[200px] truncate block cursor-pointer"
                        title="Click to view batch breakdown"
                      >
                        {r.name}
                      </button>
                    );
                  }
                  if (hasSerial) {
                    return (
                      <button
                        type="button"
                        onClick={() => handleOpenSerialDrawer(r)}
                        className="font-medium text-left text-blue-600 hover:text-blue-800 hover:underline max-w-[200px] truncate block cursor-pointer"
                        title="Click to view serial units"
                      >
                        {r.name}
                      </button>
                    );
                  }
                  return (
                    <span className="font-medium text-left text-slate-800 max-w-[200px] truncate block">
                      {r.name}
                    </span>
                  );
                },
              },
              {
                header: 'Category',
                accessor: 'category',
                className: 'text-xs text-[#64748b]',
              },
              {
                header: 'Unit',
                accessor: 'unit',
                className: 'text-xs text-[#64748b]',
              },
              {
                header: 'Opening',
                accessor: (r) => r.openingStock,
                headerClassName: 'text-right',
                className: 'text-right',
              },
              {
                header: 'In',
                accessor: (r) => (
                  <span className="text-green-600 font-medium">+{r.inQty}</span>
                ),
                headerClassName: 'text-right',
                className: 'text-right',
              },
              {
                header: 'Out',
                accessor: (r) => (
                  <span className="text-red-500 font-medium">-{r.outQty}</span>
                ),
                headerClassName: 'text-right',
                className: 'text-right',
              },
              {
                header: 'Current',
                accessor: (r) => (
                  <span className="font-bold text-[#1e293b]">
                    {r.currentStock}
                  </span>
                ),
                headerClassName: 'text-right',
                className: 'text-right',
              },
              {
                header: 'Min Level',
                accessor: 'minLevel',
                headerClassName: 'text-right',
                className: 'text-right text-[#64748b]',
              },
              {
                header: 'Stock Value',
                accessor: (r) => formatINR(r.stockValue),
                headerClassName: 'text-right',
                className: 'text-right font-medium',
              },
              {
                header: 'Status',
                accessor: (r) => {
                  const m = STATUS_META[r.status as StockStatus];
                  return (
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${m.cls}`}
                    >
                      {m.label}
                    </span>
                  );
                },
              },
              {
                header: 'Batches',
                accessor: (r) => {
                  const hasBatch = Boolean(r.isBatchTracked || r.is_batch_tracked);
                  if (!hasBatch) {
                    return <span className="text-slate-300 font-bold select-none">—</span>;
                  }
                  return (
                    <button
                      type="button"
                      onClick={() => handleOpenBatchDrawer(r)}
                      className="px-2 py-1 text-xs font-semibold rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 cursor-pointer inline-flex items-center gap-1 transition-colors"
                      title="View batch breakdown"
                    >
                      <i className="ri-archive-line text-xs" />
                      Batches
                    </button>
                  );
                },
                headerClassName: 'text-center',
                className: 'text-center',
              },
              {
                header: 'Serials',
                accessor: (r) => {
                  const hasSerial = Boolean(r.isSerialTracked || r.is_serial_tracked);
                  if (!hasSerial) {
                    return <span className="text-slate-300 font-bold select-none">—</span>;
                  }
                  return (
                    <button
                      type="button"
                      onClick={() => handleOpenSerialDrawer(r)}
                      className="px-2 py-1 text-xs font-semibold rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 cursor-pointer inline-flex items-center gap-1 transition-colors"
                      title="View serial breakdown"
                    >
                      <i className="ri-qr-code-line text-xs" />
                      Serials
                    </button>
                  );
                },
                headerClassName: 'text-center',
                className: 'text-center',
              },
            ]}
            footerRow={
              <>
                <td colSpan={9} className="px-3 py-2.5 text-xs text-[#64748b]">
                  Total ({groupedData.length} items)
                </td>
                <td className="px-3 py-2.5 text-right font-bold text-slate-800">
                  {formatINR(totalValue)}
                </td>
                <td />
                <td />
                <td />
              </>
            }
          />
        </>
      )}
      {!generated && (
        <div className="bg-white border border-[#e2e8f0] rounded-xl flex items-center justify-center py-24">
          <div className="text-center text-[#64748b]">
            <i className="ri-bar-chart-box-line text-5xl text-slate-200 block mb-3" />
            <p className="text-sm font-medium">
              Select filters and click Generate Report
            </p>
            <p className="text-xs mt-1">
              Shows current stock levels for all items
            </p>
          </div>
        </div>
      )}

      {/* ── BATCH BREAKDOWN DRAWER ── */}
      {selectedItemForBatches && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
            onClick={() => setSelectedItemForBatches(null)}
          />
          <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
            <div className="w-screen max-w-2xl bg-white shadow-2xl flex flex-col">
              {/* Header */}
              <div className="p-6 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 text-xs font-mono font-semibold">
                      {selectedItemForBatches.code || 'ITEM'}
                    </span>
                    <h2 className="text-lg font-bold text-slate-900">
                      {selectedItemForBatches.name}
                    </h2>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Batch breakdown · Total Stock: <strong className="text-slate-800">{selectedItemForBatches.currentStock} {selectedItemForBatches.unit}</strong>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedItemForBatches(null)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 cursor-pointer"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>

              {/* Expiry Alert Summary Badges */}
              <div className="px-6 py-3 bg-amber-50/50 border-b border-amber-100 flex items-center gap-4 text-xs font-medium">
                <span className="text-slate-600">Expiry Status:</span>
                <span className="flex items-center gap-1 text-red-700">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  Expired ({itemBatches.filter((b) => b.expiry_status === 'EXPIRED').length})
                </span>
                <span className="flex items-center gap-1 text-amber-700">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  &le; 30 Days ({itemBatches.filter((b) => b.expiry_status === 'EXPIRING_SOON').length})
                </span>
                <span className="flex items-center gap-1 text-emerald-700">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  Safe ({itemBatches.filter((b) => b.expiry_status === 'SAFE').length})
                </span>
              </div>

              {/* Body */}
              <div className="flex-1 p-6 overflow-y-auto">
                {loadingBatches ? (
                  <div className="py-20 text-center text-slate-400">
                    <i className="ri-loader-4-line animate-spin text-3xl mb-2 text-indigo-600" />
                    <p className="text-xs">Loading batch records...</p>
                  </div>
                ) : itemBatches.length === 0 ? (
                  <div className="py-16 text-center text-slate-400">
                    <i className="ri-archive-line text-4xl mb-2 text-slate-300 block" />
                    <p className="text-sm font-medium">No batch tracking records found for this item</p>
                    <p className="text-xs text-slate-400 mt-1">This item might not be batch-tracked or hasn't received stock via GRN.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {itemBatches.map((b) => {
                      const isExpired = b.expiry_status === 'EXPIRED';
                      const isExpiring = b.expiry_status === 'EXPIRING_SOON';
                      const isSafe = b.expiry_status === 'SAFE';

                      return (
                        <div
                          key={b.id}
                          className={`p-4 rounded-xl border transition-shadow ${
                            isExpired
                              ? 'bg-red-50/40 border-red-200'
                              : isExpiring
                              ? 'bg-amber-50/40 border-amber-200'
                              : 'bg-white border-slate-200 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-bold text-slate-900">
                                  {b.batch_number}
                                </span>
                                {b.supplier_batch_no && (
                                  <span className="text-[11px] text-slate-400 font-mono">
                                    (Vendor: {b.supplier_batch_no})
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                                <span>Warehouse: <strong className="text-slate-700">{b.warehouse_name}</strong></span>
                                <span>·</span>
                                <span>Received: {b.created_date}</span>
                              </div>
                            </div>

                            {/* Expiry Badge */}
                            <div>
                              {isExpired && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">
                                  <i className="ri-error-warning-fill text-red-500" />
                                  Expired {Math.abs(b.days_until_expiry)}d ago
                                </span>
                              )}
                              {isExpiring && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                  <i className="ri-alert-fill text-amber-600" />
                                  Expires in {b.days_until_expiry}d
                                </span>
                              )}
                              {isSafe && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <i className="ri-checkbox-circle-fill text-emerald-500" />
                                  Safe ({b.days_until_expiry}d left)
                                </span>
                              )}
                              {!isExpired && !isExpiring && !isSafe && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                                  No Expiry
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-slate-100 text-xs">
                            <div>
                              <span className="text-slate-400 block text-[10px]">Initial Qty</span>
                              <strong className="text-slate-700">{b.initial_qty} {b.unit}</strong>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[10px]">Remaining</span>
                              <strong className="text-indigo-600 font-bold">{b.current_qty} {b.unit}</strong>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[10px]">Mfg Date</span>
                              <span className="text-slate-600">{b.mfg_date || '—'}</span>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[10px]">Expiry Date</span>
                              <span className={`font-semibold ${isExpired ? 'text-red-600' : isExpiring ? 'text-amber-600' : 'text-slate-600'}`}>
                                {b.expiry_date || 'None'}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center text-xs">
                <span className="text-slate-500">
                  Total {itemBatches.length} batch(es) recorded
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedItemForBatches(null)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium cursor-pointer"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SERIAL BREAKDOWN DRAWER ── */}
      {selectedItemForSerials && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
            onClick={() => setSelectedItemForSerials(null)}
          />
          <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
            <div className="w-screen max-w-2xl bg-white shadow-2xl flex flex-col">
              {/* Header */}
              <div className="p-6 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-mono font-semibold">
                      {selectedItemForSerials.code || 'ITEM'}
                    </span>
                    <h2 className="text-lg font-bold text-slate-900">
                      {selectedItemForSerials.name}
                    </h2>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Serial units breakdown · Total Stock: <strong className="text-slate-800">{selectedItemForSerials.currentStock} {selectedItemForSerials.unit}</strong>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedItemForSerials(null)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 cursor-pointer"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>

              {/* Status Summary Badges */}
              <div className="px-6 py-3 bg-blue-50/50 border-b border-blue-100 flex items-center gap-4 text-xs font-medium">
                <span className="text-slate-600">Unit Status:</span>
                <span className="flex items-center gap-1 text-emerald-700">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  In Stock ({itemSerials.filter((s) => s.status === 'IN_STOCK').length})
                </span>
                <span className="flex items-center gap-1 text-blue-700">
                  <span className="w-2 h-2 rounded-full bg-blue-500" />
                  Sold ({itemSerials.filter((s) => s.status === 'SOLD').length})
                </span>
                <span className="flex items-center gap-1 text-slate-700">
                  <span className="w-2 h-2 rounded-full bg-slate-400" />
                  Other ({itemSerials.filter((s) => s.status !== 'IN_STOCK' && s.status !== 'SOLD').length})
                </span>
              </div>

              {/* Body */}
              <div className="flex-1 p-6 overflow-y-auto">
                {loadingSerials ? (
                  <div className="py-20 text-center text-slate-400">
                    <i className="ri-loader-4-line animate-spin text-3xl mb-2 text-blue-600" />
                    <p className="text-xs">Loading serial records...</p>
                  </div>
                ) : itemSerials.length === 0 ? (
                  <div className="py-16 text-center text-slate-400">
                    <i className="ri-qr-code-line text-4xl mb-2 text-slate-300 block" />
                    <p className="text-sm font-medium">No serial tracking records found for this item</p>
                    <p className="text-xs text-slate-400 mt-1">No individual serial numbers have been generated or received via GRN yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {itemSerials.map((s) => (
                      <div
                        key={s.id}
                        className="p-4 rounded-xl border border-slate-200 bg-white hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <i className="ri-qr-code-line text-blue-600 text-sm" />
                              <span className="font-mono text-sm font-bold text-slate-900">
                                {s.serial_number}
                              </span>
                              {(s.vendor_serial_number || s.imei) && (
                                <span className="text-[11px] text-slate-400 font-mono">
                                  (Vendor: {s.vendor_serial_number || s.imei})
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                              <span>Location: <strong className="text-slate-700">{s.status === 'SOLD' ? (s.customer_name || 'Customer') : (s.warehouse_name || 'Main Warehouse')}</strong></span>
                              <span>·</span>
                              <span>Created: {s.created_date}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {getSerialStatusBadge(s.status)}
                            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                              s.condition === 'GOOD'
                                ? 'bg-blue-50 text-blue-700'
                                : 'bg-amber-50 text-amber-700 border border-amber-200'
                            }`}>
                              {s.condition || 'GOOD'}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-100 text-xs">
                          <div>
                            <span className="text-slate-400 block text-[10px]">Warranty Expiry</span>
                            <span className="font-mono font-medium text-slate-700">{s.warranty_expiry_date || '—'}</span>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px]">Status</span>
                            <span className="font-medium text-slate-700">{s.status}</span>
                          </div>
                          <div className="text-right">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedItemForSerials(null);
                                navigate(`/inventory/serials/${s.id}`);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                            >
                              View History →
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center text-xs">
                <span className="text-slate-500">
                  Total {itemSerials.length} serial unit(s) recorded
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedItemForSerials(null);
                      navigate('/inventory/serials');
                    }}
                    className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-medium cursor-pointer transition-colors"
                  >
                    Open Master List
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedItemForSerials(null)}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium cursor-pointer"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </ReportLayout>
  );
}
